import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
  DollarSign, Activity, Hash, TrendingUp, RefreshCw, Loader2, AlertOctagon,
  Users as UsersIcon, User as UserIcon, Sparkles, BarChart3,
} from "lucide-react";

const CHART_MARGIN = { top: 8, right: 12, left: 0, bottom: 0 };
const AXIS_TICK = { fontSize: 10, fill: "#71717A" };

const fmt$ = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const fmtNum = (n) => (Number(n) || 0).toLocaleString();

function Kpi({ icon: Icon, label, value, tone = "zinc" }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4 bg-white" data-testid={`aiu-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="label-overline">{label}</div>
        <Icon className={`h-3.5 w-3.5 text-${tone}-500`} />
      </div>
      <div className="font-display font-extrabold text-2xl tracking-tight">{value}</div>
    </div>
  );
}

function BreakdownTable({ title, icon: Icon, rows, labelKey, testid }) {
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-2">
          <Icon className="h-4 w-4 text-zinc-700" />
          <h3 className="font-display font-bold text-base tracking-tight">{title}</h3>
          <Badge variant="outline" className="rounded-sm text-[10px] ml-1">{rows.length}</Badge>
        </div>
        {rows.length === 0 ? (
          <p className="text-[12px] text-zinc-500 italic py-6 text-center">No data in this window.</p>
        ) : (
          <table className="w-full text-[12px]" data-testid={testid}>
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100">
                <th className="text-left py-1.5 pr-2">{labelKey === "provider" ? "Provider" : labelKey === "model" ? "Model" : labelKey === "client_id" ? "Client" : labelKey === "user_email" ? "User" : "Scope"}</th>
                <th className="text-right py-1.5 px-2">Calls</th>
                <th className="text-right py-1.5 px-2">Tokens</th>
                <th className="text-right py-1.5 pl-2">Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 10).map((r, i) => (
                <tr key={`${r[labelKey]}-${i}`} className="border-b border-zinc-100 last:border-b-0">
                  <td className="py-1.5 pr-2 font-mono truncate max-w-[200px]" title={r[labelKey]}>{r[labelKey]}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{fmtNum(r.calls)}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-zinc-600">{fmtNum(r.total_tokens)}</td>
                  <td className="py-1.5 pl-2 text-right font-mono font-semibold">{fmt$(r.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

export default function AIUsagePage() {
  const [window, setWindow] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [tools, setTools] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, r, t] = await Promise.all([
        api.get(`/ai-usage/summary?window=${encodeURIComponent(window)}`),
        api.get("/ai-usage/recent?limit=100"),
        api.get("/ai-usage/registered-tools").catch(() => ({ data: null })),
      ]);
      setSummary(s.data); setRecent(r.data || []); setTools(t.data || null);
    } catch (e) {
      // Empty state is fine
    } finally { setRefreshing(false); }
  }, [window]);

  useEffect(() => {
    (async () => { setLoading(true); await load(); setLoading(false); })();
  }, [load]);

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading AI cost data…</div>;
  }

  const totals = summary?.totals || {};

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-7xl" data-testid="ai-usage-page">
      <div className="flex items-start justify-between gap-4 pb-4 border-b border-zinc-200">
        <div>
          <div className="label-overline">Admin · Cost Management · AI Usage</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">AI Costs</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Token usage + USD cost across every AI call (admin chat, client-portal Ask AI,
            voice agents, batch recommendations). Untracked calls are logged but cost-less
            until pricing for that model is added.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="rounded-sm h-9 w-32" data-testid="aiu-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="mtd">Month-to-date</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="rounded-sm"
            onClick={load} disabled={refreshing} data-testid="aiu-refresh">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {totals.untracked_calls > 0 && (
        <div className="flex items-start gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
          <AlertOctagon className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            {totals.untracked_calls} call{totals.untracked_calls === 1 ? "" : "s"} had no pricing data —
            they&apos;re counted in tokens but not in $ cost. Add the model to
            <code className="px-1 bg-amber-100 rounded-sm font-mono mx-1">backend/routers/ai_usage.py _PRICING</code>
            to start tracking cost.
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={DollarSign} label="Total cost" value={fmt$(totals.cost_usd)} tone="emerald" />
        <Kpi icon={Activity}   label="API calls"  value={fmtNum(totals.calls)} tone="sky" />
        <Kpi icon={Hash}       label="Total tokens" value={fmtNum(totals.total_tokens)} tone="zinc" />
        <Kpi icon={TrendingUp} label="Avg $/call"
          value={totals.calls > 0 ? fmt$((totals.cost_usd || 0) / totals.calls) : "—"} tone="indigo" />
      </div>

      {/* Daily chart */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-3">
            <BarChart3 className="h-4 w-4 text-zinc-700" />
            <h3 className="font-display font-bold text-base tracking-tight">Daily cost</h3>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.daily || []} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="day" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip formatter={(v) => fmt$(v)} />
                <Bar dataKey="cost_usd" fill="#0F172A" radius={[2, 2, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownTable title="By provider" icon={Sparkles}
          rows={summary?.by_provider || []} labelKey="provider" testid="aiu-by-provider" />
        <BreakdownTable title="By model" icon={Hash}
          rows={summary?.by_model || []} labelKey="model" testid="aiu-by-model" />
        <BreakdownTable title="By client" icon={UsersIcon}
          rows={summary?.by_client || []} labelKey="client_id" testid="aiu-by-client" />
        <BreakdownTable title="By user" icon={UserIcon}
          rows={summary?.by_user || []} labelKey="user_email" testid="aiu-by-user" />
      </div>

      {/* Recent */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-3">
            <Activity className="h-4 w-4 text-zinc-700" />
            <h3 className="font-display font-bold text-base tracking-tight">Recent AI calls</h3>
            <Badge variant="outline" className="rounded-sm text-[10px]">{recent.length}</Badge>
          </div>
          {recent.length === 0 ? (
            <p className="text-[12px] text-zinc-500 italic py-6 text-center">No AI calls logged yet.</p>
          ) : (
            <div className="max-h-[460px] overflow-y-auto">
              <table className="w-full text-[12px]" data-testid="aiu-recent-table">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-200">
                    <th className="text-left py-1.5 pr-2">When (UTC)</th>
                    <th className="text-left py-1.5 px-2">Scope</th>
                    <th className="text-left py-1.5 px-2">Model</th>
                    <th className="text-left py-1.5 px-2">User</th>
                    <th className="text-left py-1.5 px-2">Client</th>
                    <th className="text-right py-1.5 px-2">In</th>
                    <th className="text-right py-1.5 px-2">Out</th>
                    <th className="text-right py-1.5 pl-2">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.log_id} className="border-b border-zinc-100 last:border-b-0">
                      <td className="py-1.5 pr-2 font-mono text-[11px] text-zinc-500">{r.ts?.slice(0, 19).replace("T", " ")}</td>
                      <td className="py-1.5 px-2">
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${r.scope === "admin" ? "border-indigo-200 text-indigo-700 bg-indigo-50" : "border-emerald-200 text-emerald-700 bg-emerald-50"}`}>
                          {r.scope}
                        </Badge>
                      </td>
                      <td className="py-1.5 px-2 font-mono text-[11px]">{r.model || "—"}</td>
                      <td className="py-1.5 px-2 font-mono text-[11px] truncate max-w-[150px]" title={r.user_email}>{r.user_email || "—"}</td>
                      <td className="py-1.5 px-2 font-mono text-[11px] truncate max-w-[150px]" title={r.client_id}>{r.client_id || "—"}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-zinc-600">{fmtNum(r.input_tokens)}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-zinc-600">{fmtNum(r.output_tokens)}</td>
                      <td className="py-1.5 pl-2 text-right font-mono font-semibold">{r.cost_usd != null ? fmt$(r.cost_usd) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registered AI tools — safety net so any new AI feature added
          anywhere in the app shows up here, and any model that hasn't
          been priced yet is flagged in red. */}
      {tools && (
        <Card className="rounded-sm border-zinc-200" data-testid="aiu-registered-tools">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 pb-3 border-b border-zinc-100 mb-3">
              <Sparkles className="h-4 w-4 text-zinc-700" />
              <h3 className="font-display font-bold text-base tracking-tight">Registered AI tools</h3>
              <Badge variant="outline" className="rounded-sm text-[10px] ml-1">
                {tools.counts?.registered ?? 0}
              </Badge>
              {tools.counts?.missing_pricing > 0 && (
                <Badge variant="outline"
                  className="rounded-sm text-[10px] bg-red-50 text-red-700 border-red-200 ml-1"
                  data-testid="aiu-missing-pricing-badge">
                  {tools.counts.missing_pricing} missing pricing
                </Badge>
              )}
              {tools.counts?.used_unregistered > 0 && (
                <Badge variant="outline"
                  className="rounded-sm text-[10px] bg-amber-50 text-amber-800 border-amber-200 ml-1"
                  data-testid="aiu-used-unregistered-badge">
                  {tools.counts.used_unregistered} used but unregistered
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">
              Every AI capability across the platform (Creative studio,
              Assistants, Voice, Video) registers itself here on startup.
              A red row means the model isn&apos;t in
              <code className="px-1 bg-zinc-100 rounded-sm font-mono mx-1">backend/routers/ai_usage.py _PRICING</code>
              — add pricing before shipping the feature.
            </p>
            <table className="w-full text-[12px]" data-testid="aiu-registered-tools-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100">
                  <th className="text-left py-1.5 pr-2">Source</th>
                  <th className="text-left py-1.5 px-2">Kind</th>
                  <th className="text-left py-1.5 px-2">Label</th>
                  <th className="text-left py-1.5 px-2">Model</th>
                  <th className="text-right py-1.5 px-2">Calls (30d)</th>
                  <th className="text-right py-1.5 pl-2">Cost (30d)</th>
                </tr>
              </thead>
              <tbody>
                {(tools.tools || []).map((t) => (
                  <tr key={`${t.source}-${t.key}`}
                    className={`border-b border-zinc-100 last:border-b-0 ${!t.priced ? "bg-red-50/60" : ""}`}
                    data-testid={`aiu-tool-row-${t.source}-${t.key}`}>
                    <td className="py-1.5 pr-2 font-mono text-[11px]">{t.source}</td>
                    <td className="py-1.5 px-2">
                      <Badge variant="outline" className="rounded-sm text-[10px]">{t.kind}</Badge>
                    </td>
                    <td className="py-1.5 px-2">{t.label}</td>
                    <td className="py-1.5 px-2 font-mono text-[11px]">
                      {t.model}
                      {!t.priced && (
                        <Badge variant="outline"
                          className="ml-1.5 rounded-sm text-[9px] bg-red-100 text-red-800 border-red-300">
                          no pricing
                        </Badge>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-zinc-600">{fmtNum(t.calls_last_30d)}</td>
                    <td className="py-1.5 pl-2 text-right font-mono font-semibold">
                      {t.cost_last_30d ? fmt$(t.cost_last_30d) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(tools.used_unregistered || []).length > 0 && (
              <div className="mt-3 border-t border-amber-200 pt-3 text-[11px] text-amber-800 bg-amber-50/40 rounded-sm p-2">
                <div className="font-semibold mb-1">Used but not registered:</div>
                <ul className="space-y-0.5 font-mono">
                  {tools.used_unregistered.map((u) => (
                    <li key={u.model}>
                      {u.model} · {fmtNum(u.calls)} calls · {fmt$(u.cost_usd)}
                    </li>
                  ))}
                </ul>
                <div className="text-[10px] text-amber-700 mt-1.5">
                  These models are being logged but no router has called
                  <code className="px-1 bg-amber-100 rounded-sm font-mono mx-1">register_ai_tool()</code>
                  for them. Add a registration so they appear in the table above.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
